import * as Sentry from '@sentry/react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { AuthResponse, authService } from '@/shared/services/auth.service';
import {
  LoginPayload,
  LoginResult,
  RegisterPayload,
  ResendOtpPayload,
  User,
  VerifyOtpPayload,
  ActivateAccountPayload,
  ActivateAccountData,
} from '@/shared/types';
import { useNavigate, useLocation } from 'react-router-dom';
import { AxiosError } from 'axios';
import { authQueryKeys } from './queryKeys';
import { toast } from 'sonner';
import { OTP_STRINGS } from '@/shared/constants/strings';
import { useAuthCleanup } from '@/shared/hooks/useAuthCleanup';
import { getRoleBasedRedirect } from '@/shared/lib/auth.utils';

/**
 * Shared success handler for login/register/verifyOtp mutations.
 * Sets initial user data from the auth response, then hydrates the full profile
 * (roles, country, state, and consent state) BEFORE navigating so the consent
 * gate reads accurate consentStatus/needsReConsent on first render. On fetch
 * failure, the optimistic user is preserved and we still navigate so login is
 * never blocked.
 */
const useAuthSuccessHandler = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  /**
   * Validate that a redirect URL is safe for this user's roles.
   * Only allows URLs that start with known portal prefixes or home (/), or falls back to role-based default.
   */
  const isSafeRedirect = (url: string | undefined): boolean => {
    if (!url) return false;
    // Only allow safe redirects: home (/), user portal (/), admin (/admin/*), or reviewer (/reviewer/*)
    const safePatterns = ['/', '/admin', '/reviewer'];
    return safePatterns.some((pattern) => url.startsWith(pattern));
  };

  return async (data: AuthResponse) => {
    queryClient.setQueryData(authQueryKeys.currentUser, data.user);
    const from = (location.state as { from?: string } | null)?.from;

    // Validate 'from' against safe redirects; fall back to role-based if unsafe
    const redirectUrl = isSafeRedirect(from)
      ? from
      : getRoleBasedRedirect(data.user.roles ?? []);

    // Hydrate the full profile (incl. consent state) BEFORE navigating, so the
    // consent gate reads accurate consentStatus/needsReConsent on first render
    // and can't briefly fail open for a withdrawn user whose login response
    // omits those fields. On failure, keep the optimistic user (don't log out).
    try {
      const fullUser = await authService.getCurrentUser();
      if (fullUser) {
        queryClient.setQueryData(authQueryKeys.currentUser, fullUser);
      }
    } catch {
      // Swallow errors to avoid overwriting the optimistic user with null
    }

    navigate(redirectUrl || '/', { replace: true });
  };
};

export const useLogin = (): UseMutationResult<
  LoginResult,
  Error,
  LoginPayload
> => {
  const navigate = useNavigate();
  const location = useLocation();
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: authService.login,
    onSuccess: async (result) => {
      if (result.requiresMfa) {
        // Preserve the 'from' destination through the MFA flow
        const from = (location.state as { from?: string } | null)?.from;
        navigate('/verify-otp', {
          state: {
            mfaSessionId: result.mfaSessionId,
            userEmail: result.userEmail,
            userName: result.userName,
            from, // Carry through to post-OTP redirect
          },
        });
      } else {
        await onAuthSuccess({ user: result.user });
      }
    },
  });
};

export const useVerifyOtp = (): UseMutationResult<
  AuthResponse,
  Error,
  VerifyOtpPayload
> => {
  const navigate = useNavigate();
  const onAuthSuccess = useAuthSuccessHandler();

  return useMutation({
    mutationFn: authService.verifyOtp,
    onSuccess: onAuthSuccess,
    onError: (error: Error) => {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429) {
        navigate('/login', {
          state: { lockoutError: true },
        });
      }
    },
  });
};

export const useResendOtp = (): UseMutationResult<
  void,
  Error,
  ResendOtpPayload
> => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authService.resendOtp,
    onSuccess: () => {
      toast.success(OTP_STRINGS.RESEND_SUCCESS);
    },
    onError: (error: Error) => {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 429) {
        navigate('/login', { state: { lockoutError: true } });
      }
    },
  });
};

/**
 * Register a new user. Explicit GDPR consent is recorded atomically by the
 * backend via the `acceptConsent` field in the signup payload — no separate
 * consent call.
 */
export const useRegister = (): UseMutationResult<
  AuthResponse,
  Error,
  RegisterPayload
> => {
  return useMutation({
    mutationFn: authService.register,
  });
};

export const useLogout = (): UseMutationResult<void, Error, void> => {
  const cleanupAuth = useAuthCleanup();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: cleanupAuth,
  });
};

export const useCurrentUser = (): UseQueryResult<User | null, Error> => {
  return useQuery({
    queryKey: authQueryKeys.currentUser,
    queryFn: async () => {
      try {
        const user = await authService.getCurrentUser();
        return user;
      } catch (error) {
        if (error instanceof AxiosError) {
          const status = error.response?.status;

          // 401/403: User is not authenticated or forbidden
          if (status === 401 || status === 403) {
            return null;
          }

          // Network error (no response) - treat as unauthenticated
          // This prevents the app from crashing when offline
          if (!error.response) {
            Sentry.captureException(error, {
              extra: { context: 'current_user_network' },
            });
            return null;
          }

          // Server errors (5xx) - log and return null to prevent UI crash
          if (status && status >= 500) {
            Sentry.captureMessage('Server error while fetching current user', {
              level: 'error',
              extra: { status },
            });
            return null;
          }
        }

        // For unexpected errors, log and return null to maintain stability
        Sentry.captureException(error as Error, {
          extra: { context: 'current_user_unexpected' },
        });
        return null;
      }
    },
    retry: (failureCount, error) => {
      // Retry up to 2 times for network/server errors, but not for auth errors
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401 || status === 403) return false;
        if (!error.response || (status && status >= 500)) {
          return failureCount < 2;
        }
      }
      return false;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useActivateAccount = (): UseMutationResult<
  ActivateAccountData,
  Error,
  ActivateAccountPayload
> => {
  return useMutation({
    mutationFn: authService.activateAccount,
    onError: (error) => {
      Sentry.captureException(error, {
        extra: { context: 'activate_account' },
      });
    },
  });
};

export const useChangePassword = (): UseMutationResult<
  void,
  Error,
  { oldPassword: string; newPassword: string }
> => {
  return useMutation({
    mutationFn: authService.changePassword,
  });
};

export const useRequestPasswordReset = (): UseMutationResult<
  void,
  Error,
  { email: string }
> => {
  return useMutation({
    mutationFn: authService.requestPasswordReset,
  });
};

export const useCompletePasswordReset = (): UseMutationResult<
  void,
  Error,
  { token: string; newPassword: string }
> => {
  return useMutation({
    mutationFn: authService.completePasswordReset,
  });
};
